import { htParse } from "@/lib/utils";
import {
  AiSummarizeRequest,
  AiSummarizeResponse,
  Hit,
} from "@/provider/ai/type";
import { OptionsContext } from "@/provider/options-provider";
import { useContext, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CiUnlock } from "react-icons/ci";
import { AiTrigger } from "./ai-trigger";
import { AskItemForm } from "./ask-item-form";
import { getIcon } from "./get-icon";
import { useWindowDimensions } from "./hooks/window";
import { Card, CardContent, CardFooter } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Collapsible, CollapsibleContent } from "./ui/collapsible";
import { HoverCard, HoverCardContent } from "./ui/hover-card";
import { useToast } from "./ui/use-toast";

export function SearchItem(props: { item: Hit }) {
  const [summary, setSummary] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [hoverOpen, setHoverOpen] = useState<boolean>(false);
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
  const { groupSummaryItems, setGroupSummaryItems } =
    useContext(OptionsContext);
  const { toast } = useToast();

  async function summarize() {
    if (summary !== "") return;

    setLoading(true);
    const response = await fetchSummary({
      ids: [`${props.item.shortCode}${props.item.id}`],
    });
    if (response.generated_summary) {
      setSummary(response.generated_summary);
    } else {
      toast({
        title: "Error",
        description: "Failed to summarize the item",
        variant: "destructive",
      });
    }
    setLoading(false);
  }

  const { mobile } = useWindowDimensions();

  return (
    <Collapsible
      onOpenChange={async (open) => {
        if (open) {
          if (!props.item.summarizable || !props.item.shortCode) return;
          await summarize();
          setHoverOpen(false);
        }
        setSummaryOpen(open);
      }}
    >
      <Card>
        <CardContent className="m-0 flex bg-blue-200 p-0 shadow-md">
          <div className="flex justify-between p-3 align-middle">
            <div className="flex flex-row items-center gap-2">
              {/* Checkbox & icons */}
              <div className="flex flex-col items-center space-y-2 md:flex-row md:space-y-0">
                <Checkbox
                  className="m-0 mt-0.5 p-0"
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setGroupSummaryItems([
                        ...groupSummaryItems,
                        { id: props.item.id, type: props.item.shortCode },
                      ]);
                    } else {
                      setGroupSummaryItems(
                        groupSummaryItems.filter(
                          (item) => item.id !== props.item.id,
                        ),
                      );
                    }
                  }}
                  disabled={!props.item.summarizable || !props.item.shortCode}
                  checked={groupSummaryItems.some(
                    (item) => item.id === props.item.id,
                  )}
                />
                <p
                  title={`${getIcon(props.item.shortCode).alt} (${props.item.shortCode}${props.item.id})`}
                >
                  <CopyToClipboard
                    text={`${props.item.shortCode}${props.item.id}`}
                    onCopy={() => {}}
                  >
                    {getIcon(props.item.shortCode).i}
                  </CopyToClipboard>
                </p>
                <div>
                  {mobile ? (
                    <AiTrigger
                      summarizable={props.item.summarizable}
                      loading={loading}
                      setHoverOpen={setHoverOpen}
                      mobile={mobile}
                    ></AiTrigger>
                  ) : (
                    <HoverCard
                      onOpenChange={async () => {
                        if (!props.item.summarizable || !props.item.shortCode)
                          return;
                        await summarize();
                      }}
                      open={summary !== "" && hoverOpen && !summaryOpen}
                    >
                      <AiTrigger
                        summarizable={props.item.summarizable}
                        loading={loading}
                        setHoverOpen={setHoverOpen}
                        mobile={mobile}
                      ></AiTrigger>
                      {summary && (
                        <HoverCardContent
                          className="w-full bg-slate-300 lg:w-[800px]"
                          align="start"
                          side="left"
                        >
                          <div className="line-clamp-[8] h-auto w-full pl-2">
                            {htParse(summary)}
                          </div>
                        </HoverCardContent>
                      )}
                    </HoverCard>
                  )}
                </div>
                {props.item.is_restricted && <CiUnlock className="h-6 w-6" />}
              </div>
              {/* Title & Author */}
              <div>
                <p className="text-xs text-muted-foreground md:text-sm">
                  {props.item.itemDate.split("T")[0]}
                </p>
                <p>
                  <a
                    href={props.item.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs hover:text-blue-500 hover:underline md:text-sm"
                    title={`Open link in new tab`}
                  >
                    {props.item.highlight.title
                      ? htParse(props.item.highlight.title[0] ?? "")
                      : props.item.title}
                  </a>
                </p>
                <p>
                  {props.item.user && (
                    <span>
                      <span className="text-muted-foreground">by </span>
                      <span className="text-xs font-medium md:text-sm">
                        {props.item.user.initials.toUpperCase()}
                      </span>
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        {summary && (
          <CollapsibleContent>
            <CardFooter className="flex justify-center px-2 py-3 shadow-xl">
              <div className="flex w-full flex-col rounded-xl bg-slate-300 px-3 py-2 shadow-md">
                <AskItemForm
                  id={`${props.item.shortCode}${props.item.id}`}
                ></AskItemForm>
                <p className="mb-1 text-sm font-bold text-muted-foreground">
                  Summary
                </p>
                <p className="list-inside text-pretty px-0 text-xs font-semibold leading-5 md:text-sm">
                  {htParse(summary)}
                </p>
              </div>
            </CardFooter>
          </CollapsibleContent>
        )}
        {props.item.highlight.body && (
          <CardFooter>
            <div className="py-3">
              {props.item.highlight.body.map((highlight, index) => (
                <blockquote
                  key={index}
                  className="mt-6 w-[350px] overflow-hidden text-ellipsis border-l-2 pl-6 text-xs italic md:w-4/6 md:text-sm xl:w-full"
                >
                  {htParse(highlight ?? "")}
                </blockquote>
              ))}
            </div>
          </CardFooter>
        )}
      </Card>
    </Collapsible>
  );
}

export async function fetchSummary(
  aiSummarizeRequest: AiSummarizeRequest,
): Promise<AiSummarizeResponse> {
  try {
    const response = await fetch("/api/summarize", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(aiSummarizeRequest),
      cache: "default",
    });
    return await response.json();
  } catch (error) {
    console.error(`
      Client side request failed to fetch summary for ${aiSummarizeRequest.ids.concat(", ")}
      Error: ${error}
      Caching: no-cache
    `);
    return {
      generated_summary: "Something went wrong.",
    };
  }
}
